import { rpc } from "../rpc";

export type MentionSuggestion = {
  memberId: string;
  name: string;
  profilePictureUrl: string | null;
  isEveryone?: boolean;
};

type CacheEntry = {
  members: MentionSuggestion[];
  timestamp: number;
  promise?: Promise<MentionSuggestion[]>;
};

const MAX_CACHE_ENTRIES = 20;
const memberCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Get cached members for a workspace, fetching if needed.
 * Returns immediately if cached, otherwise starts fetch and returns promise.
 */
export function getCachedMembers(
  workspaceId: string,
): MentionSuggestion[] | Promise<MentionSuggestion[]> {
  const now = Date.now();
  const cached = memberCache.get(workspaceId);

  // Return cached data if fresh
  if (cached && now - cached.timestamp < CACHE_TTL_MS) {
    return cached.members;
  }

  // Return in-flight promise if already fetching
  if (cached?.promise) {
    return cached.promise;
  }

  // Start new fetch
  const promise = rpc.members
    .listForMention({ workspaceId })
    .then((members) => {
      // Evict oldest entry if cache is full
      if (
        memberCache.size >= MAX_CACHE_ENTRIES &&
        !memberCache.has(workspaceId)
      ) {
        const oldestKey = memberCache.keys().next().value;
        if (oldestKey) memberCache.delete(oldestKey);
      }
      memberCache.set(workspaceId, {
        members,
        timestamp: Date.now(),
      });
      return members;
    })
    .catch((err) => {
      console.error("Failed to prefetch members:", err);
      // Clear failed promise so it can retry
      const entry = memberCache.get(workspaceId);
      if (entry?.promise === promise) {
        memberCache.delete(workspaceId);
      }
      return [] as MentionSuggestion[];
    });

  // Store promise for deduplication
  memberCache.set(workspaceId, {
    members: cached?.members ?? [],
    timestamp: cached?.timestamp ?? 0,
    promise,
  });

  return promise;
}

/**
 * Prefetch members for a workspace (fire-and-forget).
 */
export function prefetchMembersForMention(workspaceId: string): void {
  getCachedMembers(workspaceId);
}
